import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';

export interface TourStep {
  id: string;
  targetSelector: string;
  title: string;
  message: string;
  placement: 'right' | 'bottom' | 'left' | 'top';
  roles?: string[];
}

export const TOUR_STEPS: TourStep[] = [
  {
    id: 'settings',
    targetSelector: '[data-tour="settings"]',
    title: 'Settings',
    message: 'Access your profile settings and account preferences.',
    placement: 'bottom',
  },
  {
    id: 'search',
    targetSelector: '[data-tour="search"]',
    title: 'Search',
    message: 'Search the full video library by title, artist, or topic.',
    placement: 'bottom',
  },
  {
    id: 'series',
    targetSelector: '[data-tour="series"]',
    title: 'Series',
    message: 'Follow a pre-made track to learn or explore a topic step by step.',
    placement: 'right',
  },
  {
    id: 'artists',
    targetSelector: '[data-tour="artists"]',
    title: 'Artists',
    message: 'Explore lessons from world-class jazz musicians and educators.',
    placement: 'right',
  },
  {
    id: 'library',
    targetSelector: '[data-tour="library"]',
    title: 'Library',
    message: 'Browse the full video library and filter by topic, level, or type.',
    placement: 'right',
  },
  {
    id: 'bookmarks',
    targetSelector: '[data-tour="bookmarks"]',
    title: 'Bookmarks',
    message: 'Quick access to videos you have saved for later.',
    placement: 'right',
  },
];

interface TourState {
  isActive: boolean;
  currentStep: number;
  hasCompletedTour: boolean;
  hasDismissedGuide: boolean;
}

interface TourContextValue extends TourState {
  startTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  closeTour: () => void;
  finishTour: () => void;
  dismissGuide: () => void;
  checkFirstLogin: () => void;
  currentStepData: TourStep | null;
  totalSteps: number;
}

const STORAGE_KEY = 'tenacity-tour-state';

const defaultState: TourState = {
  isActive: false,
  currentStep: 0,
  hasCompletedTour: false,
  hasDismissedGuide: false,
};

const TourContext = createContext<TourContextValue | null>(null);

function loadState(): TourState {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...defaultState, ...JSON.parse(stored) };
    }
  } catch {
    // Ignore errors
  }
  return defaultState;
}

function saveState(state: Partial<TourState>): void {
  try {
    const current = loadState();
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...current, ...state }));
  } catch {
    // Ignore errors
  }
}

interface TourProviderProps {
  children: ReactNode;
}

export function TourProvider({ children }: TourProviderProps) {
  const [state, setState] = useState<TourState>(loadState);

  const startTour = useCallback(() => {
    setState(prev => ({ ...prev, isActive: true, currentStep: 0 }));
  }, []);

  const nextStep = useCallback(() => {
    setState(prev => {
      if (prev.currentStep < TOUR_STEPS.length - 1) {
        return { ...prev, currentStep: prev.currentStep + 1 };
      }
      return prev;
    });
  }, []);

  const prevStep = useCallback(() => {
    setState(prev => {
      if (prev.currentStep > 0) {
        return { ...prev, currentStep: prev.currentStep - 1 };
      }
      return prev;
    });
  }, []);

  const closeTour = useCallback(() => {
    // Mark as completed when user closes - they've seen the tour
    setState(prev => ({ ...prev, isActive: false, hasCompletedTour: true }));
    saveState({ hasCompletedTour: true });
  }, []);

  const finishTour = useCallback(() => {
    setState(prev => ({ ...prev, isActive: false, hasCompletedTour: true }));
    saveState({ hasCompletedTour: true });
  }, []);

  const dismissGuide = useCallback(() => {
    setState(prev => ({ ...prev, hasDismissedGuide: true, isActive: false, hasCompletedTour: true }));
    saveState({ hasDismissedGuide: true, hasCompletedTour: true });
  }, []);

  const checkFirstLogin = useCallback(() => {
    const stored = loadState();
    if (!stored.hasCompletedTour && !stored.hasDismissedGuide) {
      // Small delay to let the page render first
      setTimeout(() => {
        startTour();
      }, 500);
    }
  }, [startTour]);

  const currentStepData = state.isActive ? TOUR_STEPS[state.currentStep] : null;

  const value: TourContextValue = {
    ...state,
    startTour,
    nextStep,
    prevStep,
    closeTour,
    finishTour,
    dismissGuide,
    checkFirstLogin,
    currentStepData,
    totalSteps: TOUR_STEPS.length,
  };

  return (
    <TourContext.Provider value={value}>
      {children}
    </TourContext.Provider>
  );
}

export function useTourContext() {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTourContext must be used within a TourProvider');
  }
  return context;
}
